import { Subscriptions, type Subscription } from "../../../lib/apiTyped";
import { useResource } from "../../../lib/useResource";
import { useApiCall } from "../../../lib/useToastError";
import { useNotifications } from "../../../components/shared/NotificationsProvider";
import { PageHeader } from "../../../components/AppLayout";
import { DataTable, type Column } from "../../../components/shared/DataTable";
import { StatusBadge } from "../../../components/shared/StatusBadge";
import { Button } from "../../../components/shared/Button";
import { fmtDate } from "../../../lib/format";

const LIVE = new Set(["active", "trialing", "past_due", "incomplete"]);

export function AdminSubscriptions() {
  const { data, loading, refetch } = useResource(() => Subscriptions.listAll(), []);
  const { run } = useApiCall();
  const { confirm } = useNotifications();

  async function cancelNow(orgId: string) {
    const ok = await confirm({
      title: "Cancel subscription now?",
      message:
        "The customer loses service immediately. Only do this for fraud or hard offboarding.",
      confirmLabel: "Cancel now",
      variant: "danger",
    });
    if (!ok) return;
    await run(() => Subscriptions.cancel(orgId, true), {
      successMessage: "Subscription canceled.",
    });
    await refetch();
  }

  const columns: Column<Subscription>[] = [
    { header: "Org", render: (r) => r.orgName ?? r.orgId },
    { header: "Tier", render: (r) => r.tierSlug ?? "—" },
    { header: "Seats", render: (r) => String(r.quantity ?? 0) },
    { header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { header: "Period end", render: (r) => fmtDate(r.currentPeriodEnd) },
  ];

  return (
    <>
      <PageHeader title="Subscriptions" subtitle="All API subscriptions across orgs." />
      <DataTable
        rows={data?.subscriptions ?? null}
        loading={loading}
        rowKey={(r) => r.id}
        columns={columns}
        rowActions={(r) =>
          LIVE.has(r.status) ? (
            <Button variant="link" size="sm" onClick={() => cancelNow(r.orgId)}>
              Cancel now
            </Button>
          ) : null
        }
        empty={{ title: "No subscriptions yet" }}
      />
    </>
  );
}
